import React, { useState, useEffect } from "react";
import fordStyles from "./GlobalNavBar.module.scss";
import lincolnStyles from "./GlobalNavBar-Lincoln.module.scss";
import {
  AppBar,
  Grid,
  Select,
  Toolbar,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { invalidateSessionAndRedirect } from "../../common/InvalidateSession";
import HasPermissionTo from "../../common/HasPermissionTo";
import { DEALER_SETUP, RESOURCE_CENTER } from "../../common/Constants";
import { ofType } from "../../common/PubSub";
import LincolnLogo from "../../common/LincolnLogo";
import { IS_FORD_BRAND } from "../../utils/EmpUtil";
import FordLogo from "../../common/FordLogo";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { FORD_BRAND, LINCOLN_BRAND } from "../../components/accessories/Brands";

const fullHeight = {
  minHeight: "70px",
  height: "100%",
};

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    border: "1px solid #d3d4d5",
  },
});

const StyledMenuItem = styled(MenuItem)({
  paddingLeft: "10px",
  paddingRight: "10px",
  color: "#102b4e",
});

const LincolnStyledMenuItem = styled(MenuItem)({
  paddingLeft: "10px",
  paddingRight: "10px",
  color: "#324047",
});

const FordSelect = styled(Select)({
  width: 250,
  textAlign: "left",
  borderRadius: 3,
  border: "solid 1px #4d4d4d",
  fontSize: 14,
  lineHeight: 1.43,
  letterSpacing: 1,
  color: "#102b4e",
});

const LincolnSelect = styled(Select)({
  width: 250,
  textAlign: "left",
  borderRadius: 3,
  border: "solid 1px #4d4d4d",
  fontSize: 14,
  lineHeight: 1.43,
  letterSpacing: 1,
  color: "#324047",
});

const GlobalNavBar = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [isFordBrand, setIsFordBrand] = useState(
    IS_FORD_BRAND(props.user.brand)
  );
  const [anchorElPortal, setAnchorElPortal] = useState(null);

  useEffect(() => {
    const subscription = ofType("brandChanged").subscribe(() => {
      updateBrand();
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const headerHeight = document.getElementById("appHeader").clientHeight;
    props.setPadding(headerHeight);
  }, []);

  const handleLogoutClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLogoutClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutDialogOpen = () => {
    setOpen(true);
  };

  const handleLogoutDialogClose = () => {
    setOpen(false);
  };

  const handleBrandChange = (event) => {
    props.changeBrand(event.target.value);
  };

  const handlePortalMenuClose = () => {
    setAnchorElPortal(null);
  };

  const updateBrand = () => {
    setIsFordBrand(IS_FORD_BRAND(props.user.brand));
  };

  const isActive = (currentUrl, styles) => {
    if (props.urlPath === currentUrl) {
      return styles.active;
    } else if (
      props.urlPath &&
      props.urlPath.startsWith("/pricing") &&
      currentUrl === "/pricing"
    ) {
      return styles.active;
    }
    return "";
  };

  const updateSubNavTabs = () => {
    if (!props.isNsc) {
      const user = props.user;
      if (user) {
        user.updateBusinessSettingsTab && user.updateBusinessSettingsTab(0);
        user.updateDashboardTab && user.updateDashboardTab(0);
      }
    }
  };

  const styles = isFordBrand ? fordStyles : lincolnStyles;

  const getLink = (url, displayText) => {
    return (
      <Link
        className={isActive(url, styles)}
        onClick={() => updateSubNavTabs()}
        to={{
          pathname: url,
        }}
      >
        {props.t(displayText)}
      </Link>
    );
  };

  const { user } = props;
  const name = user.name ? " (" + user.name + ")" : "";

  return (
    <AppBar
      position="fixed"
      classes={{ root: styles.globalNavBar }}
      id="appHeader"
      data-testid="appHeader"
    >
      <Toolbar style={fullHeight} disableGutters>
        <Grid container style={fullHeight}>
          <Grid item md={12} className={styles.gridItem}>
            <div className={styles.languageContainer}>
              {isFordBrand ? (
                <FordLogo className={styles.brandLogo} />
              ) : (
                <LincolnLogo className={styles.brandLogo} />
              )}
            </div>
            <div className={styles.navBarWrapper}>
              <HasPermissionTo
                perform={["dashBoardPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => (
                  <>{getLink("/dashboard", "GlobalNavBar.dashboard")}</>
                )}
              />
              <HasPermissionTo
                perform={["dealerPricingTab"]}
                permissions={user.permissions.rolePermissions}
                render={() => (
                  <>
                    {getLink("/pricing/dealerpricing", "GlobalNavBar.pricing")}
                  </>
                )}
              />

              <HasPermissionTo
                perform={["businessSettingsPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => (
                  <>{getLink("/business-settings", "GlobalNavBar.settings")}</>
                )}
              />
              <HasPermissionTo
                perform={["reportingPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => (
                  <>{getLink("/reporting", "GlobalNavBar.reporting")}</>
                )}
              />

              <Menu
                className={styles.userSection}
                id="portal-menu"
                anchorEl={anchorElPortal}
                keepMounted
                open={Boolean(anchorElPortal)}
                onClose={() => handlePortalMenuClose()}
                onClick={() => handlePortalMenuClose()}
                disableAutoFocusItem={true}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <HasPermissionTo
                  perform={["dealerSetupLink"]}
                  permissions={user.permissions.rolePermissions}
                  render={() => (
                    <a
                      target="_blank"
                      href={DEALER_SETUP}
                      className={styles.logoutFont}
                      rel="noreferrer"
                    >
                      <MenuItem className={styles.logoutFont}>
                        {props.t("GlobalNavBar.dealerSetup")}
                      </MenuItem>
                    </a>
                  )}
                />

                <HasPermissionTo
                  perform={["resourceCenterLink"]}
                  permissions={user.permissions.rolePermissions}
                  render={() => (
                    <a
                      target="_blank"
                      href={RESOURCE_CENTER}
                      className={styles.logoutFont}
                      rel="noreferrer"
                    >
                      <MenuItem className={styles.logoutFont}>
                        {props.t("GlobalNavBar.resourceCenter")}
                      </MenuItem>
                    </a>
                  )}
                />
              </Menu>
            </div>
            <div className={styles.flex} />
            <HasPermissionTo
              perform={["franchiseSelector"]}
              permissions={user.permissions.rolePermissions}
              condition={
                props.user.dealerGroup && props.user.dealerGroup.length > 1
              }
              render={() => (
                <ChangeBrand
                  {...props}
                  styles={styles}
                  brand={props.user.brand}
                  handleBrandChange={handleBrandChange}
                  isFordBrand={isFordBrand}
                />
              )}
            />
            <Logout
              {...props}
              styles={styles}
              handleLogoutClick={handleLogoutClick}
              user={user}
              name={name}
              anchorEl={anchorEl}
              handleLogoutClose={handleLogoutClose}
              handleLogoutDialogOpen={handleLogoutDialogOpen}
              open={open}
              handleLogoutDialogClose={handleLogoutDialogClose}
            />
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

const Logout = (props) => {
  return (
    <div>
      <div className={props.styles.dealerContainer}>
        <div
          className={`${props.styles.dealerBox} ${props.styles.withPointer}`}
          onClick={props.handleLogoutClick}
        >
          <HasPermissionTo
            perform={["NADealerDetails:view"]}
            permissions={props.user.permissions.rolePermissions}
            condition={props.user.userType === "na_dealer"}
            render={() => {
              return (
                <NADealerDetails
                  {...props}
                  styles={props.styles}
                  dealerType={props.dealerType}
                  name={props.name}
                />
              );
            }}
            noRender={() => {
              return (
                <DealerDetails
                  user={props.user}
                  name={props.name}
                  styles={props.styles}
                />
              );
            }}
          />
          <KeyboardArrowDown id="logout-menu" className={props.styles.arrow} />
        </div>
      </div>
      <StyledMenu
        className={props.styles.userSection}
        id="customized-menu"
        disableAutoFocusItem
        anchorEl={props.anchorEl}
        keepMounted
        open={Boolean(props.anchorEl)}
        onClose={props.handleLogoutClose}
        elevation={10}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <StyledMenuItem
          onClick={props.handleLogoutDialogOpen}
          to={{ pathname: "/" }}
        >
          <span className={props.styles.logoutFont}>
            {props.t("Logout.logout")}
          </span>
        </StyledMenuItem>
      </StyledMenu>

      <Dialog
        open={props.open}
        onClose={props.handleLogoutDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <span className={props.styles.logoutConfirmationFont}>
              {props.t("Logout.confirmation")}
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.handleLogoutDialogClose} color="primary">
            {props.t("Logout.decline")}
          </Button>
          <Button
            onClick={invalidateSessionAndRedirect}
            color="primary"
            autoFocus
          >
            {props.t("Logout.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const DealerDetails = (props) => {
  return (
    <div id="name">
      {props.user.fullName}
      {props.user.userType === "dealer" && props.name}
    </div>
  );
};

const NADealerDetails = (props) => {
  const meta = props.user.commonDealerId.split("|");
  return (
    <div className={props.styles.dealerDetails} id="name">
      <span className={props.styles.dealerType}>
        {props.dealerType || props.t("GlobalNavBar.dealer")}
      </span>
      <span className={props.styles.dealerName}>{props.user.dealerName}</span>
      <span className={props.styles.dealerCommonId}>
        {meta[1].concat(" | ").concat(meta[2])}
      </span>
    </div>
  );
};

const ChangeBrand = (props) => {
  const StyledSelect = props.isFordBrand ? FordSelect : LincolnSelect;
  const StyledMenuOption = props.isFordBrand
    ? StyledMenuItem
    : LincolnStyledMenuItem;

  return (
    <StyledSelect
      native={props.native}
      variant="outlined"
      value={props.brand}
      onChange={props.handleBrandChange}
      data-testid="change-brand"
      inputProps={{
        "data-testid": "actual-brand",
      }}
      IconComponent={() => (
        <KeyboardArrowDown className={props.styles.brandIcon} />
      )}
    >
      <StyledMenuOption value={FORD_BRAND} className={props.styles.arrow}>
        &nbsp;&nbsp;{props.t("GlobalNavBar.fordBrand")}
      </StyledMenuOption>
      <StyledMenuOption value={LINCOLN_BRAND} className={props.styles.arrow}>
        &nbsp;&nbsp;{props.t("GlobalNavBar.lincolnBrand")}
      </StyledMenuOption>
    </StyledSelect>
  );
};

export default withTranslation("emp")(GlobalNavBar);
